import { useConnectModal } from '@rainbow-me/rainbowkit';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAccount, useDisconnect } from 'wagmi';
import { getWalletClient, switchChain } from 'wagmi/actions';
import { base } from 'wagmi/chains';
import type { Address } from 'viem';
import { wagmiConfig } from './wagmi';

export type BaseWalletClient = Awaited<ReturnType<typeof getWalletClient>>;

type WalletState = {
  address?: Address;
  connecting: boolean;
  chainId?: number;
  error?: string;
  connect(): Promise<void>;
  disconnect(): void;
  getClient(): Promise<BaseWalletClient>;
};

const WalletContext = createContext<WalletState | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const account = useAccount();
  const { disconnect: disconnectWagmi } = useDisconnect();
  const { openConnectModal } = useConnectModal();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (account.address) setError(undefined);
  }, [account.address]);

  const connect = useCallback(async () => {
    setError(undefined);
    if (!openConnectModal) {
      const value = new Error('Wallet connection is not ready yet.');
      setError(value.message);
      throw value;
    }
    openConnectModal();
  }, [openConnectModal]);

  const disconnect = useCallback(() => {
    disconnectWagmi();
    setError(undefined);
  }, [disconnectWagmi]);

  const getClient = useCallback(async () => {
    if (!account.address) throw new Error('Connect a wallet first.');
    try {
      if (account.chainId !== base.id) await switchChain(wagmiConfig, { chainId: base.id });
      return await getWalletClient(wagmiConfig, { chainId: base.id });
    } catch (value) {
      const message = value instanceof Error ? value.message : 'Unable to connect the wallet to Base.';
      setError(message);
      throw value;
    }
  }, [account.address, account.chainId]);

  const value = useMemo(
    () => ({
      address: account.address,
      connecting: account.status === 'connecting' || account.status === 'reconnecting',
      chainId: account.chainId,
      error,
      connect,
      disconnect,
      getClient,
    }),
    [account.address, account.chainId, account.status, error, connect, disconnect, getClient],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const value = useContext(WalletContext);
  if (!value) throw new Error('useWallet must be used inside WalletProvider');
  return value;
}
